import { useCallback, useContext } from "react";
import { commitMutation, graphql } from "react-relay";

import type { Event } from "~/calendar/CalendarPage";
import recurrenceChoiceModal from "~/calendar/EventForm/recurrenceChoiceModal";
import { confirm } from "~/lib/prompts";
import { environment } from "~/models";
import OngoingMeetingContext from "~/OngoingMeetingContext";
import toast from "~/ui/toast";

import type {
	EventEditType,
	useDeleteEventMutation as MutationType,
} from "~/__generated__/useDeleteEventMutation.graphql";

const mutation = graphql`
	mutation useDeleteEventMutation($calendar: ID!, $guid: String!, $editType: EventEditType!) {
		deleteEvent(calendarId: $calendar, guid: $guid, editType: $editType) {
			event {
				guid
			}
		}
	}
`;

export type EventToDelete = Pick<Event, "guid" | "type"> & { calendar: Pick<Event["calendar"], "id"> };

export default function useDeleteEvent(event?: EventToDelete) {
	const { ongoingMeeting, dismissPrompt } = useContext(OngoingMeetingContext);

	return useCallback(async (): Promise<string | null> => {
		if (!event) return null;

		let editType: EventEditType = "THIS";

		if (event.type !== "SINGLE") {
			const selectedEditType = await recurrenceChoiceModal();
			if (!selectedEditType) {
				return null;
			}

			editType = selectedEditType;
		} else if (!(await confirm("Are you sure you want to delete this event?", { title: "Delete Event" }))) {
			return null;
		}

		commitMutation<MutationType>(environment, {
			mutation,
			variables: {
				calendar: event.calendar.id,
				guid: event.guid,
				editType: editType,
			},
		});

		toast.info("Event deleted");

		if (ongoingMeeting?.guid == event.guid) {
			// The meeting is no longer "ongoing". Dismiss the prompt.
			dismissPrompt();
		}

		return event.guid;
	}, [event, ongoingMeeting, dismissPrompt]);
}
